import React from 'react';
import { useLocation } from 'react-router-dom';
import { useMobileView } from '@/hooks/useMobileDetection';
import { MobileDashboard } from './MobileDashboard';
import { MobilePOSView } from './MobilePOSView';
import { MobileProductsView } from './MobileProductsView';

// Mapeo de rutas a componentes móviles
const MOBILE_COMPONENTS = {
  '/admin': MobileDashboard,
  '/sales/pos': MobilePOSView,
  '/inventory/products': MobileProductsView,
  // Agregar más rutas móviles aquí
};

interface MobileRouterProps {
  children: React.ReactNode;
}

export function MobileRouter({ children }: MobileRouterProps) {
  const { shouldUseMobileView } = useMobileView();
  const location = useLocation();

  // Si no es móvil, renderizar componentes normales
  if (!shouldUseMobileView) {
    return <>{children}</>;
  }

  // Buscar componente móvil específico para la ruta actual
  const MobileComponent = MOBILE_COMPONENTS[location.pathname as keyof typeof MOBILE_COMPONENTS];

  // Si existe componente móvil específico, usarlo
  if (MobileComponent) {
    return <MobileComponent />;
  }

  // Fallback a componentes normales con optimizaciones móviles
  return (
    <div className="mobile-optimized">
      {children}
    </div>
  );
}

// Wrapper para páginas que tienen versión móvil específica
export function withMobileVersion<T extends object>(
  DesktopComponent: React.ComponentType<T>,
  MobileComponent: React.ComponentType<T>
) {
  return function WrappedComponent(props: T) {
    const { shouldUseMobileView } = useMobileView();
    
    if (shouldUseMobileView) {
      return <MobileComponent {...props} />;
    }
    
    return <DesktopComponent {...props} />;
  };
}

// HOC para componentes adaptativos
export function withResponsiveDesign<T extends object>(
  Component: React.ComponentType<T>
) {
  return function ResponsiveComponent(props: T) {
    const { deviceType, shouldUseMobileView, shouldUseTabletView } = useMobileView();
    
    return (
      <div 
        className={`responsive-container ${deviceType}`}
        data-mobile={shouldUseMobileView}
        data-tablet={shouldUseTabletView}
      >
        <Component {...props} />
      </div>
    );
  };
}